import { RadioGroup } from '@headlessui/react';
import React from 'react';
import Image, { ImageProps } from '../elements/image/image';
import { isNone, isSome, Maybe } from '@crocodille/common';
import cn from 'classnames';

import styles from './radio-select.module.scss';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from '@utils/format';

export type RadioSelectChild<V> = React.ReactElement<
  RadioSelectOptionProps<V>,
  typeof Option
>;

export interface RadioSelectProps<V> {
  readonly value: V;
  onChange(value: V): void;
  readonly children: RadioSelectChild<V> | readonly RadioSelectChild<V>[];
  readonly collapsed?: boolean;
  readonly withBackground?: boolean;
}

function RadioSelect<V = unknown>({
  value,
  onChange,
  children,
  collapsed = false,
  withBackground = false,
}: RadioSelectProps<V>) {
  return (
    <RadioGroup
      className={cn(styles.container, {
        [styles.isCollapsed]: collapsed,
        [styles.withBackground]: withBackground,
      })}
      value={value}
      onChange={onChange}
    >
      {children}
    </RadioGroup>
  );
}

const OptionCheck: React.FC<{
  readonly checked: boolean;
  readonly className?: string;
}> = ({ checked, className }) => {
  return (
    <div
      className={cn(styles.check, { [styles.isChecked]: checked }, className)}
    ></div>
  );
};

export interface RadioSelectOptionProps<V> {
  readonly value: V;
  readonly disabled?: boolean;
  readonly image?: Maybe<ImageProps['src']>;
  readonly rotateImage?: boolean;
  readonly label: string;
  readonly description?: Maybe<string>;
  readonly price?: Maybe<number>;
  readonly priceAdjustment?: boolean;
  readonly largeImage?: boolean;
}

function Option<V = unknown>({
  value,
  disabled,
  image,
  rotateImage = false,
  label,
  description,
  price,
  priceAdjustment,
  children,
  largeImage,
}: React.PropsWithChildren<RadioSelectOptionProps<V>>) {
  const {
    i18n: { language },
  } = useTranslation();

  return (
    <RadioGroup.Option
      value={value}
      disabled={disabled}
      className={({ active, checked, disabled }) =>
        cn(styles.option, {
          [styles.isActive]: active,
          [styles.isChecked]: checked,
          [styles.isDisabled]: disabled,
          [styles.isWithoutDescription]: isNone(description),
        })
      }
    >
      {({ checked }) => (
        <>
          <div
            className={cn(styles.imageContainer, {
              [styles.largeImage]: largeImage,
            })}
          >
            <div
              className={cn(styles.image, { [styles.isRotated]: rotateImage })}
            >
              <Image
                role="presentation"
                src={image}
                alt={label}
                width="37"
                height="37"
                sizes="37px"
                objectFit="contain"
              />
            </div>
            {children}
          </div>
          <span className={styles.label}>{label}</span>
          {isSome(description) ? (
            <span className={styles.description}>{description}</span>
          ) : null}
          {isSome(price) ? (
            <span className={styles.price}>
              {formatCurrency(price, 'CZK', language, {
                signDisplay: priceAdjustment ? 'exceptZero' : 'auto',
              })}
            </span>
          ) : null}
          <OptionCheck checked={checked} />
        </>
      )}
    </RadioGroup.Option>
  );
}

export default Object.assign(RadioSelect, { Option });
